import axios from 'axios';

class LinkedInAuthService {
  constructor() {
    this.accessToken = null;
  }

  async initializeAuth() {
    const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
    
    if (!clientId || clientId === 'your_linkedin_client_id_here') {
      throw new Error('LinkedIn Client ID not configured. Please set REACT_APP_LINKEDIN_CLIENT_ID in your .env file.');
    }
    
    const redirectUri = `${window.location.origin}/auth/linkedin/callback`;
    const state = this.generateState();
    
    // Store state for callback verification
    localStorage.setItem('linkedin_oauth_state', state);
    
    const scopes = [
      'r_liteprofile',
      'r_emailaddress',
      'w_member_social',
      'r_organization_social',
      'w_organization_social',
      'rw_organization_admin'
    ].join(' ');

    const authUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
      `response_type=code&` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `state=${state}&` +
      `scope=${encodeURIComponent(scopes)}`;

    return {
      authUrl,
      clientId,
      redirectUri,
      state
    };
  }

  generateState() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  async handleAuthCallback(code, state) {
    try {
      const storedState = localStorage.getItem('linkedin_oauth_state');
      if (state !== storedState) {
        throw new Error('Invalid state parameter');
      }

      const clientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID;
      const clientSecret = process.env.REACT_APP_LINKEDIN_CLIENT_SECRET;
      const redirectUri = `${window.location.origin}/auth/linkedin/callback`;

      const tokenResponse = await axios.post('https://www.linkedin.com/oauth/v2/accessToken', 
        new URLSearchParams({
          grant_type: 'authorization_code',
          code: code,
          client_id: clientId,
          client_secret: clientSecret,
          redirect_uri: redirectUri
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      );

      this.accessToken = tokenResponse.data.access_token;

      // Store tokens securely
      const tokens = {
        access_token: this.accessToken,
        expires_in: tokenResponse.data.expires_in,
        token_type: tokenResponse.data.token_type,
        scope: tokenResponse.data.scope
      };

      localStorage.setItem('linkedin_tokens', JSON.stringify(tokens));
      localStorage.removeItem('linkedin_oauth_state');

      return tokens;
    } catch (error) {
      console.error('LinkedIn Auth callback error:', error);
      throw error;
    }
  }

  async getUserProfile() {
    try {
      if (!this.accessToken) {
        const storedTokens = localStorage.getItem('linkedin_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
        } else {
          throw new Error('LinkedIn access token not found');
        }
      }

      // Get basic profile
      const profileResponse = await axios.get('https://api.linkedin.com/v2/people/~', {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        params: {
          projection: '(id,firstName,lastName,profilePicture(displayImage~:playableStreams))'
        }
      });

      // Get email address
      const emailResponse = await axios.get('https://api.linkedin.com/v2/emailAddress', {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        params: {
          q: 'members',
          projection: '(elements*(handle~))'
        }
      });

      const profile = profileResponse.data;
      const email = emailResponse.data.elements?.[0]?.['handle~']?.emailAddress;

      return this.formatUserProfile(profile, email);
    } catch (error) {
      console.error('Error fetching LinkedIn user profile:', error);
      throw error;
    }
  }

  formatUserProfile(profile, email) {
    const firstName = profile.firstName?.localized?.en_US || '';
    const lastName = profile.lastName?.localized?.en_US || '';
    const profilePicture = profile.profilePicture?.['displayImage~']?.elements?.[0]?.identifiers?.[0]?.identifier;

    return {
      id: profile.id,
      name: `${firstName} ${lastName}`.trim(),
      firstName,
      lastName,
      email,
      profileImageUrl: profilePicture,
      source: 'linkedin'
    };
  }

  async getCompanyPages() {
    try {
      if (!this.accessToken) {
        const storedTokens = localStorage.getItem('linkedin_tokens');
        if (storedTokens) {
          const tokens = JSON.parse(storedTokens);
          this.accessToken = tokens.access_token;
        } else {
          throw new Error('LinkedIn access token not found');
        }
      }

      const response = await axios.get('https://api.linkedin.com/v2/organizationAcls', {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        params: {
          q: 'roleAssignee',
          role: 'ADMINISTRATOR',
          projection: '(elements*(organization~(id,name,description,website,industry,specialties,locations,logoV2(original~:playableStreams),coverPhotoV2(original~:playableStreams))))'
        }
      });

      const organizations = response.data.elements || [];
      return this.formatCompanyData(organizations);
    } catch (error) {
      console.error('Error fetching LinkedIn company pages:', error);
      return [];
    }
  }

  formatCompanyData(organizations) {
    return organizations.map(org => {
      const company = org['organization~'];
      const logo = company.logoV2?.['original~']?.elements?.[0]?.identifiers?.[0]?.identifier;
      const cover = company.coverPhotoV2?.['original~']?.elements?.[0]?.identifiers?.[0]?.identifier;
      const location = company.locations?.[0];

      return {
        id: company.id,
        name: company.name,
        description: company.description,
        website: company.website,
        industry: company.industry,
        specialties: company.specialties || [],
        logoUrl: logo,
        coverImageUrl: cover,
        location: location ? {
          country: location.country,
          city: location.city,
          description: location.description
        } : null,
        source: 'linkedin'
      };
    });
  }

  async postToCompany(companyId, text, mediaUrn = null) {
    try {
      if (!this.accessToken) {
        throw new Error('LinkedIn access token not found');
      }

      const postData = {
        author: `urn:li:organization:${companyId}`,
        lifecycleState: 'PUBLISHED',
        specificContent: {
          'com.linkedin.ugc.ShareContent': {
            shareCommentary: {
              text: text
            },
            shareMediaCategory: mediaUrn ? 'IMAGE' : 'NONE'
          }
        },
        visibility: {
          'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC'
        }
      };

      if (mediaUrn) {
        postData.specificContent['com.linkedin.ugc.ShareContent'].media = [
          {
            status: 'READY',
            description: {
              text: text
            },
            media: mediaUrn,
            title: {
              text: 'Share'
            }
          }
        ];
      }

      const response = await axios.post('https://api.linkedin.com/v2/ugcPosts', postData, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json',
          'X-Restli-Protocol-Version': '2.0.0'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error posting to LinkedIn company:', error);
      throw error;
    }
  }

  async uploadImage(imageFile) {
    try {
      if (!this.accessToken) {
        throw new Error('LinkedIn access token not found');
      }

      // Register upload
      const registerResponse = await axios.post('https://api.linkedin.com/v2/assets', 
        {
          registerUploadRequest: {
            recipes: ['urn:li:digitalmediaRecipe:feedshare-image'],
            owner: 'urn:li:person:CURRENT_USER',
            serviceRelationships: [
              {
                relationshipType: 'OWNER',
                identifier: 'urn:li:userGeneratedContent'
              }
            ]
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const uploadUrl = registerResponse.data.value.uploadMechanism['com.linkedin.digitalmedia.uploading.MediaUploadHttpRequest'].uploadUrl;
      const asset = registerResponse.data.value.asset;

      // Upload image
      await axios.put(uploadUrl, imageFile, {
        headers: {
          'Content-Type': imageFile.type
        }
      });

      return asset;
    } catch (error) {
      console.error('Error uploading image to LinkedIn:', error);
      throw error;
    }
  }

  async getCompanyFollowers(companyId) {
    try {
      if (!this.accessToken) {
        throw new Error('LinkedIn access token not found');
      }

      const response = await axios.get(`https://api.linkedin.com/v2/networkSizes/urn:li:organization:${companyId}`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        },
        params: {
          edgeType: 'COMPANY_FOLLOWED_BY_MEMBER'
        }
      });

      return response.data.firstDegreeSize || 0;
    } catch (error) {
      console.error('Error fetching LinkedIn company followers:', error);
      return 0;
    }
  }

  clearTokens() {
    localStorage.removeItem('linkedin_tokens');
    localStorage.removeItem('linkedin_oauth_state');
    this.accessToken = null;
  }

  isAuthenticated() {
    const tokens = localStorage.getItem('linkedin_tokens');
    return !!tokens;
  }
}

export default LinkedInAuthService;